import { IconGift, IconHelp } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/components/sui/sidebar'
import { useAffiliateEntry } from '@/hooks/use-affiliate-entry'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { openGleap } from '@/integrations/gleap'
import { useContextStore } from '@/stores/context'

import { NotificationsPopover } from '../notifications-popover'
import { SidebarMenuButtonWithTooltip } from '../sidebar-menu-button-with-tooltip'

import locales from './locales'

interface NavSecondaryProps {
  unreadNotifications?: number
  onAffiliateActivationClick?: () => void
}

export function NavSecondary({
  unreadNotifications = 0,
  onAffiliateActivationClick,
}: NavSecondaryProps) {
  const { t } = useComponentTranslation(locales)
  const { setOpenMobile } = useSidebar()
  const accountContext = useContextStore((state) => state.userAccountContext)

  const hasSupportChatDisabled =
    accountContext?.accountInformation.hasSupportChatDisabled ?? false

  // Hidden outright for the accounts the program never belongs to: managed
  // ones, sessions managing someone else's account, blocked ones, and anyone
  // who is not an administrator of their own account.
  const affiliateEntry = useAffiliateEntry()

  // Nobody has joined a program they have not been told about: for an account
  // that has not activated it, the entry opens the explainer instead of a
  // panel with nothing in it. While the gate read is still in flight it
  // navigates, and the page's own landing takes over.
  const opensActivation = affiliateEntry.isJoined === false

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:p-0">
      <SidebarMenu>
        {!hasSupportChatDisabled && (
          <SidebarMenuItem>
            <SidebarMenuButtonWithTooltip
              render={
                <button
                  type="button"
                  onClick={() => {
                    setOpenMobile(false)
                    openGleap()
                  }}
                />
              }
              tooltip={t('help')}
              size="sm"
            >
              <IconHelp />
              <span>{t('help')}</span>
            </SidebarMenuButtonWithTooltip>
          </SidebarMenuItem>
        )}
        <NotificationsPopover unreadCount={unreadNotifications} />
        {affiliateEntry.isVisible && (
          <SidebarMenuItem>
            <SidebarMenuButtonWithTooltip
              render={
                opensActivation ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpenMobile(false)
                      onAffiliateActivationClick?.()
                    }}
                  />
                ) : (
                  // The section root, exactly like Reports: its index route
                  // redirects to the first page, so navigation is identical
                  // whichever page the program opens on.
                  <Link to="/affiliate" onClick={() => setOpenMobile(false)} />
                )
              }
              tooltip={t('referAndEarn')}
              size="sm"
            >
              {/* A gift, not a money glyph: coin stack, banknote and card are
                  already Commissions, Withdrawals and Payout methods one click
                  away, and the network glyph read as abstract. */}
              <IconGift />
              <span>{t('referAndEarn')}</span>
            </SidebarMenuButtonWithTooltip>
          </SidebarMenuItem>
        )}
      </SidebarMenu>
    </SidebarGroup>
  )
}
